// src/Redux/wishlistSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchWishlistByCustomer,
  addWishlistItem,
  removeWishlistItem,
} from '../services/LabTestApi/Wishlist/AddWishlist';

// ============================================================
// 1. FETCH WISHLIST
// ============================================================
export const fetchWishlist = createAsyncThunk(
  'wishlist/fetchWishlist',
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) {
        return [];
      }
      const data = await fetchWishlistByCustomer(userId);
      return data.data || [];
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch wishlist');
    }
  }
);

// ============================================================
// 2. ADD TO WISHLIST
// ============================================================
export const addToWishlist = createAsyncThunk(
  'wishlist/addToWishlist',
  async ({ userId, productId, categoryId }, { rejectWithValue }) => {
    try {
      const result = await addWishlistItem({
        CustomerId: userId,
        ProductId: productId,
        CategoryId: categoryId,
      });
      return { productId, categoryId, result };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add to wishlist');
    }
  }
);

// ============================================================
// 3. REMOVE FROM WISHLIST
// ============================================================
export const removeFromWishlist = createAsyncThunk(
  'wishlist/removeFromWishlist',
  async ({ userId, productId, categoryId }, { rejectWithValue }) => {
    try {
      const result = await removeWishlistItem(userId, productId, categoryId);
      return { productId, categoryId, result };
    } catch (error) {
      // ✅ Even on error, remove from local state for better UX
      return { productId, categoryId, result: { success: true } };
    }
  }
);

// ============================================================
// 4. INITIAL STATE
// ============================================================
const initialState = {
  items: [],
  loading: false,
  error: null,
  addingLoading: false,
  removingLoading: false,
  initialized: false,
};

// ============================================================
// 5. SLICE
// ============================================================
const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    clearWishlist: (state) => {
      state.items = [];
      state.error = null;
      state.initialized = false;
    },
    resetWishlistErrors: (state) => {
      state.error = null;
    },
    addWishlistOptimistic: (state, action) => {
      const { productId, categoryId } = action.payload;
      if (!state.items.some(item => 
        Number(item.productId) === Number(productId) && 
        Number(item.categoryId) === Number(categoryId)
      )) {
        state.items.push({ productId, categoryId });
      }
    },
    removeWishlistOptimistic: (state, action) => {
      const { productId, categoryId } = action.payload;
      state.items = state.items.filter(
        item => !(Number(item.productId) === Number(productId) && 
                 Number(item.categoryId) === Number(categoryId))
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.initialized = true;
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.initialized = true;
      })
      .addCase(addToWishlist.pending, (state) => {
        state.addingLoading = true;
        state.error = null;
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.addingLoading = false;
        const { productId, categoryId } = action.payload;
        if (!state.items.some(item => 
          Number(item.productId) === Number(productId) && 
          Number(item.categoryId) === Number(categoryId)
        )) {
          state.items.push({ productId, categoryId });
        }
      })
      .addCase(addToWishlist.rejected, (state, action) => {
        state.addingLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(removeFromWishlist.pending, (state) => {
        state.removingLoading = true;
        state.error = null;
      })
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        state.removingLoading = false;
        const { productId, categoryId } = action.payload;
        state.items = state.items.filter(
          item => !(Number(item.productId) === Number(productId) && 
                   Number(item.categoryId) === Number(categoryId))
        );
      })
      .addCase(removeFromWishlist.rejected, (state, action) => {
        state.removingLoading = false;
        // ✅ Always remove from local state even on API error
        const { productId, categoryId } = action.meta.arg;
        state.items = state.items.filter(
          item => !(Number(item.productId) === Number(productId) && 
                   Number(item.categoryId) === Number(categoryId))
        );
        state.error = action.payload || action.error.message;
      });
  },
});

export const { 
  clearWishlist, 
  resetWishlistErrors,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} = wishlistSlice.actions;

export default wishlistSlice.reducer;